import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import CheckBox from '@/components/basicComponents/checkbox';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input, { ErrorText } from '@/components/basicComponents/input';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { editInvestorEntityDetailsTaxStatusOptions } from '@/components/investors-page/editInvestorPage/constants';
import { EditInvestorEntityDetailsTaxStatusOptionEnum } from '@/components/investors-page/editInvestorPage/types';
import FooterModal from '@/components/settings-page/importData/footer';
import { useResponsive } from '@/hooks/use-responsive';
import { buildFormTestId } from '@/lib/utils/testIds';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { Category, Eligibility, EntityType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WizardProgressBar } from '../../../basicComponents/wizard/progressBar';
import { ENTITY_CATEGORIES_QUERY } from '../queries';
import { TwoColumnWrapper } from '../templateComponents/styles';

export interface IInvestorDetailsStepForm {
  category: keyof typeof Category;
  taxStatus: EditInvestorEntityDetailsTaxStatusOptionEnum;
  entityType: keyof typeof EntityType;
  otherType: string;
  eligibility: (keyof typeof Eligibility)[];
}

export const investorDetailsStepDefaultValues: IInvestorDetailsStepForm = {
  category: null,
  taxStatus: EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAXABLE,
  entityType: null,
  otherType: '',
  eligibility: []
};

// Form-specific helper for generating test IDs using the utility function
const buildTestId = (section: string, field: string, part?: string) =>
  buildFormTestId('signature-packet', 'investor-details', section, field, part);

const eligibilityOptions: (keyof typeof Eligibility)[] = Object.keys(Eligibility) as (keyof typeof Eligibility)[];

interface InvestorDetailsStepProps {
  mode: 'documentationTab' | 'wizard';
  investorDetailsStepData: IInvestorDetailsStepForm;
  signaturePacketWizardSteps?: { name: string; status: string }[];
  saveInvestorDetailsData: (data: IInvestorDetailsStepForm) => Promise<void>;
  onCancel?: () => void;
}

export const InvestorDetailsStep = ({
  mode,
  investorDetailsStepData,
  signaturePacketWizardSteps,
  saveInvestorDetailsData,
  onCancel
}: InvestorDetailsStepProps) => {
  const { isMobile } = useResponsive();
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
    handleSubmit
  } = useForm<IInvestorDetailsStepForm>({ defaultValues: investorDetailsStepDefaultValues });

  const { t } = useTranslation(['enums', 'common', 'generateSignaturePacketPage']);

  const [categorySelectOptions, setCategorySelectOptions] = useState<ISelectValue[]>([]);
  const [entityTypeSelectOptions, setEntityTypeSelectOptions] = useState<ISelectValue[]>([]);
  const [showOtherTypeInput, setShowOtherTypeInput] = useState(false);

  const createEntityTypeSelectOptions = (entityTypes: (keyof typeof EntityType)[]) => {
    return entityTypes.map((entityType) => createSimpleSelectOption(entityType, t(`enums:EntityType.${entityType}`)));
  };

  const createCategorySelectOptions = (categories: (keyof typeof Category)[]) => {
    return categories.map((category) => createSimpleSelectOption(category, t(`enums:Category.${category}`)));
  };

  useEffect(() => {
    reset((defaultFormValues) => ({ ...defaultFormValues, ...investorDetailsStepData }));
    setShowOtherTypeInput(investorDetailsStepData.entityType === getEnumKeyByValue(EntityType, EntityType.Other));
  }, [investorDetailsStepData]);

  useEffect(() => {
    if (!entityTypeSelectOptions.length) return;
    const selectedEntityType = entityTypeSelectOptions.some((option) => option.value === watch('entityType'));
    if (!selectedEntityType) {
      setValue('entityType', null);
    }
  }, [entityTypeSelectOptions]);

  const { data: entityCategoriesData, loading: entityCategoriesLoading } = useQuery<{
    renderingTemplateCategories: { category: keyof typeof Category; entityTypes: (keyof typeof EntityType)[] }[];
  }>(ENTITY_CATEGORIES_QUERY, {
    onCompleted: (data) => {
      const categoryOptions = createCategorySelectOptions(data?.renderingTemplateCategories?.map((categoryItem) => categoryItem.category) ?? []);
      setCategorySelectOptions(categoryOptions ?? []);
      if (investorDetailsStepData?.category) {
        const selectedCategory = data?.renderingTemplateCategories.find((option) => option.category === investorDetailsStepData.category);
        setEntityTypeSelectOptions(createEntityTypeSelectOptions(selectedCategory?.entityTypes ?? []));
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  return (
    <>
      <StepWrapper isMobile={isMobile} mode={mode}>
        {signaturePacketWizardSteps && <WizardProgressBar steps={signaturePacketWizardSteps} />}
        <InvestorDetailsStepWrapper>
          <div>
            <SectionHeader>{t('generateSignaturePacketPage:investorDetails.taxStatus')}</SectionHeader>
            <Controller
              name="taxStatus"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {editInvestorEntityDetailsTaxStatusOptions.map((option, index) => (
                    <RadioButton
                      key={option + index}
                      name="taxStatus"
                      radioButtonText={option}
                      selectedOption={value}
                      handleRadioChange={(event) => onChange(event.target.value)}
                      data-testid={buildTestId('tax-info', 'taxStatus', option)}
                    />
                  ))}
                </>
              )}
            />
          </div>
          <TwoColumnWrapper isMobile={isMobile}>
            <Controller
              name="category"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Category is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select
                    value={value}
                    setValue={(value) => {
                      onChange(value);
                      if (value !== watch('category')) {
                        setValue('entityType', null);
                      }
                      const selectedCategory = entityCategoriesData.renderingTemplateCategories.find((option) => option.category === value);
                      setEntityTypeSelectOptions(createEntityTypeSelectOptions(selectedCategory?.entityTypes ?? []));
                    }}
                    options={categorySelectOptions}
                    loading={entityCategoriesLoading}
                    errorText={error?.message}
                    label={t('generateSignaturePacketPage:investorDetails.entityCategory')}
                    isRequiredField
                    size="md"
                    data-testid={buildTestId('entity-info', 'category')}
                    data-testid-dropdown={buildTestId('entity-info', 'category', 'dropdown')}
                    data-testid-option={buildTestId('entity-info', 'category', 'option')}
                  />
                </>
              )}
            />
            <Controller
              name="entityType"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Entity Type is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select
                    value={value}
                    setValue={(value) => {
                      onChange(value);
                      setShowOtherTypeInput(watch('entityType') === getEnumKeyByValue(EntityType, EntityType.Other));
                    }}
                    options={entityTypeSelectOptions}
                    loading={entityCategoriesLoading}
                    errorText={error?.message}
                    label={t('generateSignaturePacketPage:investorDetails.entityType')}
                    styleType={!watch('category') ? 'disabled' : 'default'}
                    size="md"
                    isRequiredField
                    data-testid={buildTestId('entity-info', 'entityType')}
                    data-testid-dropdown={buildTestId('entity-info', 'entityType', 'dropdown')}
                    data-testid-option={buildTestId('entity-info', 'entityType', 'option')}
                  />
                </>
              )}
            />
          </TwoColumnWrapper>
          {showOtherTypeInput && (
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="otherType"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Entity Type is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    label={t('generateSignaturePacketPage:investorDetails.entityType')}
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    errorText={error?.message}
                    size="md"
                    isRequiredField
                    data-testid={buildTestId('entity-info', 'otherType')}
                  />
                )}
              />
            </TwoColumnWrapper>
          )}
          <TwoColumnWrapper isMobile={isMobile}>
            <div>
              <Controller
                name="eligibility"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'At least one selection is required to continue'
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <>
                    {error?.message && <ErrorText size="sm">{error.message}</ErrorText>}
                    <SectionHeader>{t('generateSignaturePacketPage:investorDetails.eligibility')}</SectionHeader>
                    {eligibilityOptions.map((item) => (
                      <CheckboxWrapper
                        key={item}
                        id={item}
                        onClick={() => {
                          const updatedEligibility = value.includes(item) ? value.filter((el) => el !== item) : [...value, item];
                          onChange(updatedEligibility);
                        }}
                      >
                        <CheckBox id={item} isActive={value.includes(item)} data-testid={buildTestId('eligibility', item)} />
                        {t(`enums:Eligibility.${item}`)}
                      </CheckboxWrapper>
                    ))}
                  </>
                )}
              />
            </div>
            <Banner
              title=""
              description={
                mode === 'documentationTab'
                  ? t('generateSignaturePacketPage:investorDetails.documentationTabBannerDescription')
                  : t('generateSignaturePacketPage:investorDetails.wizardBannerDescription')
              }
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.1')}
              color={themeVar('font.base')}
            />
          </TwoColumnWrapper>
        </InvestorDetailsStepWrapper>
      </StepWrapper>
      {mode === 'documentationTab' && (
        <FormFooter
          onCancel={onCancel}
          onSave={handleSubmit(saveInvestorDetailsData)}
          disableSaveButton={!!Object.keys(errors || {}).length}
          showRequiredFieldsWarning={false}
        />
      )}
      {mode === 'wizard' && (
        <FooterModal
          startOver={onCancel}
          startOverButtonName="Cancel"
          confirmButton={{
            name: t('common:saveAndContinue'),
            onClick: handleSubmit(saveInvestorDetailsData),
            disabled: !!Object.keys(errors || {}).length
          }}
        />
      )}
    </>
  );
};

const StepWrapper = styled.div<{ isMobile: boolean; mode: 'documentationTab' | 'wizard' }>`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: ${({ mode }) => (mode === 'wizard' ? '30px 0 150px 0' : '30px 0 10px 0')};
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

const InvestorDetailsStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const SectionHeader = styled.h2`
  font-size: 19px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 4px;
  color: ${themeVar('font.base')};
`;

const CheckboxWrapper = styled.div`
  cursor: pointer;
  display: flex;
  gap: 9px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 10px;
`;
